import { useState, useEffect } from "react";
import api from "../api";
import CustomSelect from "../CustomSelect";
import { Bounce, toast } from "react-toastify";

const TrainerEsperienze = ({ esperienza, operatorView }) => {
  const [esperienzeState, setEsperienzeState] = useState(esperienza || []);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({});
  const [mansioni, setMansioni] = useState([]);

  useEffect(() => {
    fetchMansioni();
  }, []);

  const fetchMansioni = async () => {
    try {
      const res = await api.get("/mansioni");
      if (res.status === 200) setMansioni(res.data);
    } catch (err) {
      console.error("Errore fetch mansioni", err);
      toast.error("Errore nel recupero delle mansioni", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
    }
  };

  const toISODate = (dateStr) => {
    if (!dateStr) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split(".");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    return dateStr;
  };

  const handleEdit = (id) => {
    const exp = esperienzeState.find((e) => e.idEsperienzaTrainer === id);
    if (!exp) return;

    setForm({
      [id]: {
        ruolo: exp.ruolo || "",
        mansioneId: exp.mansione?.idMansione || "",
        dal: exp.dal || "",
        al: exp.al || "",
        nomeAzienda: exp.nomeAzienda || "",
        descrizioneRuolo: exp.descrizioneRuolo || "",
      },
    });

    setEditingId(id);
  };

  const handleCancel = (id) => {
    setEditingId(null);
    setErrors((prev) => {
      const copy = { ...prev };
      Object.keys(copy).forEach((k) => {
        if (k.startsWith(`${id}-`)) delete copy[k];
      });
      return copy;
    });
  };

  const handleChange = (id, field, value) => {
    setForm((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
    setErrors((prev) => ({ ...prev, [`${id}-${field}`]: undefined }));
  };

  const validate = (id) => {
    const inputErrors = {};
    const f = form[id];
    if (!f.ruolo.trim()) inputErrors[`${id}-ruolo`] = "Ruolo obbligatorio";
    if (!f.mansioneId)
      inputErrors[`${id}-mansioneId`] = "Mansione obbligatoria";
    if (!f.dal) inputErrors[`${id}-dal`] = "Data inizio obbligatoria";
    if (!f.nomeAzienda.trim())
      inputErrors[`${id}-nomeAzienda`] = "Nome azienda obbligatorio";
    if (!f.descrizioneRuolo.trim())
      inputErrors[`${id}-descrizioneRuolo`] = "Descrizione obbligatoria";
    setErrors((prev) => ({ ...prev, ...inputErrors }));
    return Object.keys(inputErrors).length === 0;
  };

  const handleSave = async (id) => {
    if (!validate(id)) return;

    const f = form[id];
    const payload = {
      ruolo: f.ruolo.toUpperCase(),
      idMansione: f.mansioneId,
      dal: toISODate(f.dal),
      al: toISODate(f.al),
      nomeAzienda: f.nomeAzienda,
      descrizioneRuolo: f.descrizioneRuolo,
      trainerCodiceFiscale:
        esperienza.trainerCodiceFiscale ||
        esperienzeState.find((e) => e.idEsperienzaTrainer === id)
          ?.trainerCodiceFiscale ||
        "",
    };

    try {
      setLoading((prev) => ({ ...prev, [id]: true }));
      const res = await api.put(`/esperienze-lavorative/${id}`, payload);
      if (res.status === 200 || res.status === 204) {
        setEsperienzeState((prev) =>
          prev.map((e) =>
            e.idEsperienzaTrainer === id
              ? {
                  ...e,
                  ...f,
                  mansione: mansioni.find((m) => m.idMansione === f.mansioneId),
                }
              : e
          )
        );
        setEditingId(null);
        toast.success("Esperienza aggiornata!", {
          position: "top-right",
          autoClose: 3000,
          transition: Bounce,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Errore durante il salvataggio!", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
    } finally {
      setLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  return (
    <div className="trainer-esperienze-lavorative">
      <div className="trainer-esperienze-lavorative-title">
        Esperienze Lavorative
      </div>
      <div className="trainer-data-fields">
        {esperienzeState.length === 0 ? (
          <div className="no-data">Nessuna esperienza lavorativa.</div>
        ) : (
          esperienzeState.map((exp) => {
            const id = exp.idEsperienzaTrainer;
            const isEditing = editingId === id;

            const mansioneOptions = mansioni.map((m) => ({
              value: m.idMansione,
              label: m.nomeMansione,
            }));

            const selectedMansione = mansioneOptions.find(
              (m) =>
                m.value === (form[id]?.mansioneId || exp.mansione?.idMansione)
            );

            return (
              <div className="trainer-data" key={id}>
                {/* Ruolo */}
                <div className="trainer-field-row">
                  <div className="trainer-field-label">Ruolo:</div>
                  <div className="trainer-field-value">
                    {isEditing ? (
                      <>
                        <CustomSelect
                          options={[
                            { value: "TITOLARE", label: "Titolare" },
                            { value: "DIPENDENTE", label: "Dipendente" },
                            { value: "CONSULENTE", label: "Consulente" },
                            { value: "DOCENTE", label: "Docente" },
                          ]}
                          value={{
                            value: form[id]?.ruolo || exp.ruolo,
                            label: form[id]?.ruolo || exp.ruolo,
                          }}
                          onChange={(sel) =>
                            handleChange(id, "ruolo", sel?.value || "")
                          }
                        />
                        {errors[`${id}-ruolo`] && (
                          <span className="error-msg">
                            {errors[`${id}-ruolo`]}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="trainer-span-value">{exp.ruolo}</span>
                    )}
                  </div>
                </div>

                {/* Nome mansione */}
                <div className="trainer-field-row">
                  <div className="trainer-field-label">Nome mansione:</div>
                  <div className="trainer-field-value">
                    {isEditing ? (
                      <>
                        <CustomSelect
                          options={mansioneOptions}
                          value={selectedMansione}
                          onChange={(sel) =>
                            handleChange(id, "mansioneId", sel?.value || "")
                          }
                        />
                        {errors[`${id}-mansioneId`] && (
                          <span className="error-msg">
                            {errors[`${id}-mansioneId`]}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="trainer-span-value">
                        {exp.mansione?.nomeMansione}
                      </span>
                    )}
                  </div>
                </div>

                {/* Dal */}
                <div className="trainer-field-row">
                  <div className="trainer-field-label">Dal:</div>
                  <div className="trainer-field-value">
                    {isEditing ? (
                      <>
                        <input
                          type="date"
                          value={toISODate(form[id]?.dal || exp.dal)}
                          onChange={(e) =>
                            handleChange(id, "dal", e.target.value)
                          }
                        />
                        {errors[`${id}-dal`] && (
                          <span className="error-msg">
                            {errors[`${id}-dal`]}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="trainer-span-value">{exp.dal}</span>
                    )}
                  </div>
                </div>

                {/* Al */}
                <div className="trainer-field-row">
                  <div className="trainer-field-label">Al:</div>
                  <div className="trainer-field-value">
                    {isEditing ? (
                      <input
                        type="date"
                        value={toISODate(form[id]?.al || exp.al)}
                        onChange={(e) => handleChange(id, "al", e.target.value)}
                      />
                    ) : (
                      <span className="trainer-span-value">{exp.al}</span>
                    )}
                  </div>
                </div>

                {/* Nome azienda */}
                <div className="trainer-field-row">
                  <div className="trainer-field-label">Nome Azienda:</div>
                  <div className="trainer-field-value">
                    {isEditing ? (
                      <>
                        <input
                          value={form[id]?.nomeAzienda || exp.nomeAzienda}
                          onChange={(e) =>
                            handleChange(id, "nomeAzienda", e.target.value)
                          }
                        />
                        {errors[`${id}-nomeAzienda`] && (
                          <span className="error-msg">
                            {errors[`${id}-nomeAzienda`]}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="trainer-span-value">
                        {exp.nomeAzienda}
                      </span>
                    )}
                  </div>
                </div>

                {/* Descrizione */}
                <div className="trainer-field-row">
                  <div className="trainer-field-label">Descrizione Ruolo:</div>
                  <div className="trainer-field-value">
                    {isEditing ? (
                      <>
                        <textarea
                          value={
                            form[id]?.descrizioneRuolo || exp.descrizioneRuolo
                          }
                          onChange={(e) =>
                            handleChange(id, "descrizioneRuolo", e.target.value)
                          }
                        />
                        {errors[`${id}-descrizioneRuolo`] && (
                          <span className="error-msg">
                            {errors[`${id}-descrizioneRuolo`]}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="trainer-span-value">
                        {exp.descrizioneRuolo}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                {!operatorView && (
                  <div className="trainer-edit-actions">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          className="save-btn"
                          onClick={() => handleSave(id)}
                        >
                          Salva
                        </button>
                        <button
                          type="button"
                          className="cancel-btn"
                          onClick={() => handleCancel(id)}
                        >
                          Annulla
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="edit-btn"
                        onClick={() => handleEdit(id)}
                        disabled={editingId !== null}
                      >
                        Modifica
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TrainerEsperienze;
